import type { ExpressionSpecification, GeoJSONSource } from 'mapbox-gl';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { type RelatedVesselsFilter, useCdcRelatedVessels, useCdcVessel } from '../hooks/useCdcVessels';
import { useCdcStore } from '../stores/useCdcStore';

const CDC_CONNECTIONS_SOURCE = 'cdc-connections-source';
const CDC_CONNECTIONS_LAYER = 'cdc-connections-layer';
const CDC_RELATED_MARKERS_SOURCE = 'cdc-related-markers-source';
const CDC_RELATED_MARKERS_LAYER = 'cdc-related-markers-layer';

// Colors for different filter combinations
const FILTER_COLORS: Record<string, string> = {
  nearby: '#00ff88', // Green
  same_type: '#ff8800', // Orange
  same_destination: '#00aaff', // Blue
  same_flag: '#ff00aa', // Pink
  same_owner: '#aa00ff', // Purple
  multi: '#ffff00', // Yellow for multiple filters
};

// Icon size multiplier for related vessels (larger than normal)
const RELATED_ICON_SIZE_MULTIPLIER = 1.8;

// Ship type to icon mapping (same as CdcVesselLayer)
const SHIP_TYPE_ICON_EXPRESSION: ExpressionSpecification = [
  'match',
  ['coalesce', ['get', 'ship_type'], 0],
  70,
  'cargo',
  71,
  'cargo',
  72,
  'cargo',
  73,
  'cargo',
  74,
  'cargo',
  75,
  'cargo',
  76,
  'cargo',
  77,
  'cargo',
  78,
  'cargo',
  79,
  'cargo',
  80,
  'tanker',
  81,
  'tanker',
  82,
  'tanker',
  83,
  'tanker',
  84,
  'tanker',
  85,
  'tanker',
  86,
  'tanker',
  87,
  'tanker',
  88,
  'tanker',
  89,
  'tanker',
  60,
  'passenger',
  61,
  'passenger',
  62,
  'passenger',
  63,
  'passenger',
  64,
  'passenger',
  65,
  'passenger',
  66,
  'passenger',
  67,
  'passenger',
  68,
  'passenger',
  69,
  'passenger',
  40,
  'high_speed_craft',
  41,
  'high_speed_craft',
  42,
  'high_speed_craft',
  43,
  'high_speed_craft',
  44,
  'high_speed_craft',
  45,
  'high_speed_craft',
  46,
  'high_speed_craft',
  47,
  'high_speed_craft',
  48,
  'high_speed_craft',
  49,
  'high_speed_craft',
  20,
  'wing_in_ground',
  21,
  'wing_in_ground',
  22,
  'wing_in_ground',
  23,
  'wing_in_ground',
  24,
  'wing_in_ground',
  25,
  'wing_in_ground',
  26,
  'wing_in_ground',
  27,
  'wing_in_ground',
  28,
  'wing_in_ground',
  29,
  'wing_in_ground',
  36,
  'passenger',
  37,
  'passenger',
  30,
  'other_type',
  31,
  'other_type',
  32,
  'other_type',
  33,
  'other_type',
  34,
  'other_type',
  35,
  'other_type',
  50,
  'other_type',
  51,
  'other_type',
  52,
  'other_type',
  53,
  'other_type',
  54,
  'other_type',
  55,
  'other_type',
  56,
  'other_type',
  57,
  'other_type',
  58,
  'other_type',
  59,
  'other_type',
  90,
  'other_type',
  91,
  'other_type',
  92,
  'other_type',
  93,
  'other_type',
  94,
  'other_type',
  95,
  'other_type',
  96,
  'other_type',
  97,
  'other_type',
  98,
  'other_type',
  99,
  'other_type',
  'unknown_type',
];

/**
 * CdcConnectionLines - Draws lines from selected vessel to related vessels
 */
export const CdcConnectionLines: React.FC = () => {
  const { map, isMapReady } = useMapbox();
  const [layerAdded, setLayerAdded] = useState(false);

  const selectedMmsi = useCdcStore((state) => state.selectedMmsi);
  const selectedVesselCoords = useCdcStore((state) => state.selectedVesselCoords);
  const activeFilters = useCdcStore((state) => state.activeRelatedFilters);
  const setRelatedVesselMmsis = useCdcStore((state) => state.setRelatedVesselMmsis);
  const nearbyRadiusKm = useCdcStore((state) => state.nearbyRadiusKm);

  // Get selected vessel details for coordinates (fallback for initial selection)
  const { data: selectedVesselData } = useCdcVessel(selectedMmsi);

  // Build filter object based on active filters
  const filter: RelatedVesselsFilter | null = useMemo(() => {
    const hasAnyFilter =
      activeFilters.nearby ||
      activeFilters.same_type ||
      activeFilters.same_destination ||
      activeFilters.same_flag ||
      activeFilters.same_owner;
    if (!hasAnyFilter) return null;

    return {
      nearby: activeFilters.nearby,
      same_type: activeFilters.same_type,
      same_destination: activeFilters.same_destination,
      same_flag: activeFilters.same_flag,
      same_owner: activeFilters.same_owner,
      radius_km: nearbyRadiusKm,
      limit: 100,
    };
  }, [activeFilters, nearbyRadiusKm]);

  // Fetch related vessels
  const { data: relatedVessels } = useCdcRelatedVessels(selectedMmsi, filter);

  // Update store with related vessel MMSIs (for filtering from main layer)
  useEffect(() => {
    if (!relatedVessels?.features?.length) {
      setRelatedVesselMmsis(new Set<string>());
      return;
    }

    const mmsis = new Set<string>();
    for (const feature of relatedVessels.features) {
      const mmsi = feature.properties?.mmsi as string | undefined;
      if (mmsi) {
        mmsis.add(mmsi);
      }
    }
    setRelatedVesselMmsis(mmsis);
  }, [relatedVessels, setRelatedVesselMmsis]);

  // Count active filters for coloring
  const activeFilterCount = useMemo(() => Object.values(activeFilters).filter(Boolean).length, [activeFilters]);

  // Generate connection lines GeoJSON
  const linesGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    const emptyCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    // Need active filters and related vessels
    if (activeFilterCount === 0 || !relatedVessels?.features?.length) {
      return emptyCollection;
    }

    // Prefer store coords (updated from bbox data) over useCdcVessel data
    // Store coords are more up-to-date when vessel position refreshes
    let selectedCoords: [number, number] | null = null;

    if (selectedVesselCoords) {
      selectedCoords = [selectedVesselCoords.lon, selectedVesselCoords.lat];
    } else if (selectedVesselData?.found && selectedVesselData.vessel) {
      const vessel = selectedVesselData.vessel;
      selectedCoords = [vessel.lon, vessel.lat];
    }

    if (!selectedCoords) {
      return emptyCollection;
    }

    // Create a line from selected vessel to each related vessel
    const lines = relatedVessels.features.map((feature) => {
      const geometry = feature.geometry as GeoJSON.Point;
      const relatedCoords = geometry.coordinates as [number, number];

      return {
        type: 'Feature' as const,
        properties: {
          mmsi: feature.properties?.mmsi,
          name: feature.properties?.name,
        },
        geometry: {
          type: 'LineString' as const,
          coordinates: [selectedCoords, relatedCoords],
        },
      };
    });

    return {
      type: 'FeatureCollection',
      features: lines,
    };
  }, [selectedVesselCoords, selectedVesselData, activeFilterCount, relatedVessels]);

  // Generate markers GeoJSON for related vessels (point features)
  const markersGeoJson = useMemo<GeoJSON.FeatureCollection>(() => {
    if (activeFilterCount === 0 || !relatedVessels?.features?.length) {
      return { type: 'FeatureCollection', features: [] };
    }
    // Return the related vessels as point features (they already are)
    return relatedVessels;
  }, [activeFilterCount, relatedVessels]);

  // Get line color based on active filters
  const lineColor = useMemo(() => {
    if (activeFilterCount === 0) return '#00ff88';
    if (activeFilterCount > 1) return FILTER_COLORS.multi;

    // Single filter - find which one
    const activeKey = Object.entries(activeFilters).find(([, v]) => v)?.[0];
    return activeKey ? FILTER_COLORS[activeKey] || '#00ff88' : '#00ff88';
  }, [activeFilters, activeFilterCount]);

  // Add/update the connection lines and markers layers
  useEffect(() => {
    if (!isMapReady || !map.current) return;

    const mapInstance = map.current;

    // Add source and layer if not already added
    if (!layerAdded) {
      // Connection lines source and layer
      if (!mapInstance.getSource(CDC_CONNECTIONS_SOURCE)) {
        mapInstance.addSource(CDC_CONNECTIONS_SOURCE, {
          type: 'geojson',
          data: linesGeoJson,
        });
      }

      if (!mapInstance.getLayer(CDC_CONNECTIONS_LAYER)) {
        mapInstance.addLayer({
          id: CDC_CONNECTIONS_LAYER,
          type: 'line',
          source: CDC_CONNECTIONS_SOURCE,
          paint: {
            'line-color': lineColor,
            'line-width': 2,
            'line-opacity': 0.7,
          },
        });
      }

      // Related vessel markers source and layer (larger icons)
      if (!mapInstance.getSource(CDC_RELATED_MARKERS_SOURCE)) {
        mapInstance.addSource(CDC_RELATED_MARKERS_SOURCE, {
          type: 'geojson',
          data: markersGeoJson,
        });
      }

      if (!mapInstance.getLayer(CDC_RELATED_MARKERS_LAYER)) {
        mapInstance.addLayer({
          id: CDC_RELATED_MARKERS_LAYER,
          type: 'symbol',
          source: CDC_RELATED_MARKERS_SOURCE,
          layout: {
            'icon-image': SHIP_TYPE_ICON_EXPRESSION,
            'icon-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              0,
              0.03 * RELATED_ICON_SIZE_MULTIPLIER,
              5,
              0.06 * RELATED_ICON_SIZE_MULTIPLIER,
              10,
              0.08 * RELATED_ICON_SIZE_MULTIPLIER,
            ],
            'icon-rotate': ['coalesce', ['get', 'true_heading'], ['get', 'cog'], 0],
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
          },
        });
      }

      setLayerAdded(true);
    } else {
      // Update connection lines source data
      const linesSource = mapInstance.getSource(CDC_CONNECTIONS_SOURCE) as GeoJSONSource | undefined;
      if (linesSource) {
        linesSource.setData(linesGeoJson);
      }

      // Update markers source data
      const markersSource = mapInstance.getSource(CDC_RELATED_MARKERS_SOURCE) as GeoJSONSource | undefined;
      if (markersSource) {
        markersSource.setData(markersGeoJson);
      }

      // Update line color based on filter type
      if (mapInstance.getLayer(CDC_CONNECTIONS_LAYER)) {
        mapInstance.setPaintProperty(CDC_CONNECTIONS_LAYER, 'line-color', lineColor);
      }
    }
  }, [isMapReady, map, linesGeoJson, markersGeoJson, layerAdded, lineColor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map.current) return;
      const mapInstance = map.current;

      try {
        // Remove layers first, then sources
        if (mapInstance.getLayer(CDC_RELATED_MARKERS_LAYER)) {
          mapInstance.removeLayer(CDC_RELATED_MARKERS_LAYER);
        }
        if (mapInstance.getLayer(CDC_CONNECTIONS_LAYER)) {
          mapInstance.removeLayer(CDC_CONNECTIONS_LAYER);
        }
        if (mapInstance.getSource(CDC_RELATED_MARKERS_SOURCE)) {
          mapInstance.removeSource(CDC_RELATED_MARKERS_SOURCE);
        }
        if (mapInstance.getSource(CDC_CONNECTIONS_SOURCE)) {
          mapInstance.removeSource(CDC_CONNECTIONS_SOURCE);
        }
      } catch {
        // Map may already be destroyed
      }
    };
  }, [map]);

  return null;
};
